import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { ClickUpSourceSettingsApi } from "./ClickUpSourceSettings.Api";
import type { ClickUpSourceSettingsViewModel } from "./ClickUpSourceSettings.ViewModel.types";
import type {
  ClickUpConfiguration,
  ClickUpFolder,
  ClickUpList,
  ClickUpSpace,
  ClickUpStatus,
  ClickUpWorkspace,
} from "./ClickUpSourceSettings.types";

const NONE_OPTION = "__none";

const normalizeId = (value: unknown): string => String(value ?? "");

export const useClickUpSourceSettingsViewModel = (
  model: SourceSettingsModel,
): ClickUpSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const autoSelectedWorkspaceForConnectionRef = useRef<string | null>(null);
  const autoSelectedSpaceForSelectionRef = useRef<string | null>(null);
  const autoSelectedListForSelectionRef = useRef<string | null>(null);
  const seededDefaultStatusesForSelectionRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );

  const [workspaces, setWorkspaces] = useState<ClickUpWorkspace[]>([]);
  const [spaces, setSpaces] = useState<ClickUpSpace[]>([]);
  const [folders, setFolders] = useState<ClickUpFolder[]>([]);
  const [lists, setLists] = useState<ClickUpList[]>([]);
  const [statuses, setStatuses] = useState<ClickUpStatus[]>([]);
  const [workspacesLoaded, setWorkspacesLoaded] = useState(false);
  const [spacesLoaded, setSpacesLoaded] = useState(false);
  const [foldersLoaded, setFoldersLoaded] = useState(false);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [statusesLoaded, setStatusesLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.ClickUp && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<ClickUpConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  const selectedWorkspaceId = normalizeId(configuration.workspace?.id);
  const selectedSpaceId = normalizeId(configuration.space?.id);
  const selectedFolderId = normalizeId(configuration.folder?.id);
  const selectedListId = normalizeId(configuration.list?.id);
  const configurationRef = useRef(configuration);

  useEffect(() => {
    configurationRef.current = configuration;
  }, [configuration]);

  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      autoSelectedWorkspaceForConnectionRef.current = null;
      autoSelectedSpaceForSelectionRef.current = null;
      autoSelectedListForSelectionRef.current = null;
      seededDefaultStatusesForSelectionRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    updateConnectionConfiguration<ClickUpConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        workspace: cfg.workspace,
        space: cfg.space,
        folder: cfg.folder,
        list: cfg.list,
        statuses: cfg.statuses ?? [],
        doneStatus: cfg.doneStatus,
      }),
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  useEffect(() => {
    const currentProfileIdentity =
      `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setSpaces([]);
    setFolders([]);
    setLists([]);
    setStatuses([]);
    autoSelectedWorkspaceForConnectionRef.current = null;
    autoSelectedSpaceForSelectionRef.current = null;
    autoSelectedListForSelectionRef.current = null;
    seededDefaultStatusesForSelectionRef.current = null;

    updateConnectionConfiguration<ClickUpConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        workspace: undefined,
        space: undefined,
        folder: undefined,
        list: undefined,
        statuses: [],
        doneStatus: undefined,
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  useEffect(() => {
    const loadWorkspaces = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId) {
        setWorkspaces([]);
        setWorkspacesLoaded(true);
        return;
      }

      const clickUpApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<ClickUpSourceSettingsApi>(
          SourceTypeIds.ClickUp,
        );
      if (!clickUpApi) {
        setWorkspaces([]);
        setWorkspacesLoaded(true);
        return;
      }

      setWorkspacesLoaded(false);
      setHasError(false);
      try {
        const nextWorkspaces = await clickUpApi.getWorkspaces(
          model.boardId,
          editingConnectionId,
        );
        setWorkspaces(nextWorkspaces);

        if (
          !selectedWorkspaceId &&
          nextWorkspaces.length > 0 &&
          autoSelectedWorkspaceForConnectionRef.current !== editingConnectionId
        ) {
          autoSelectedWorkspaceForConnectionRef.current = editingConnectionId;
          updateConnectionConfiguration<ClickUpConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            (cfg) =>
              normalizeId(cfg.workspace?.id)
                ? cfg
                : {
                    ...cfg,
                    workspace: nextWorkspaces[0],
                    space: undefined,
                    folder: undefined,
                    list: undefined,
                    statuses: [],
                    doneStatus: undefined,
                  },
            { isDefault: true },
          );
        }
      } catch {
        setWorkspaces([]);
        setHasError(true);
      } finally {
        setWorkspacesLoaded(true);
      }
    };

    void loadWorkspaces();
  }, [editingConnectionId, isVisible, model.boardId, selectedWorkspaceId]);

  useEffect(() => {
    const loadSpaces = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId || !selectedWorkspaceId) {
        setSpaces([]);
        setSpacesLoaded(true);
        return;
      }

      const clickUpApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<ClickUpSourceSettingsApi>(
          SourceTypeIds.ClickUp,
        );
      if (!clickUpApi) {
        setSpaces([]);
        setSpacesLoaded(true);
        return;
      }

      setSpacesLoaded(false);
      setHasError(false);
      try {
        const nextSpaces = await clickUpApi.getSpaces(
          model.boardId,
          editingConnectionId,
          selectedWorkspaceId,
        );
        setSpaces(nextSpaces);

        const selectionKey = `${editingConnectionId}:${selectedWorkspaceId}`;
        if (
          !selectedSpaceId &&
          nextSpaces.length > 0 &&
          autoSelectedSpaceForSelectionRef.current !== selectionKey
        ) {
          autoSelectedSpaceForSelectionRef.current = selectionKey;
          updateConnectionConfiguration<ClickUpConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            // Each default below fills only the selection it was loaded for,
            // so it is safe to apply to the saved settings too.
            (cfg) =>
              normalizeId(cfg.workspace?.id) !== selectedWorkspaceId ||
              normalizeId(cfg.space?.id)
                ? cfg
                : {
                    ...cfg,
                    space: nextSpaces[0],
                    folder: undefined,
                    list: undefined,
                    statuses: [],
                    doneStatus: undefined,
                  },
            { isDefault: true },
          );
        }
      } catch {
        setSpaces([]);
        setHasError(true);
      } finally {
        setSpacesLoaded(true);
      }
    };

    void loadSpaces();
  }, [editingConnectionId, isVisible, model.boardId, selectedSpaceId, selectedWorkspaceId]);

  useEffect(() => {
    const loadFolders = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId || !selectedSpaceId) {
        setFolders([]);
        setFoldersLoaded(true);
        return;
      }

      const clickUpApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<ClickUpSourceSettingsApi>(
          SourceTypeIds.ClickUp,
        );
      if (!clickUpApi) {
        setFolders([]);
        setFoldersLoaded(true);
        return;
      }

      setFoldersLoaded(false);
      setHasError(false);
      try {
        setFolders(
          await clickUpApi.getFolders(model.boardId, editingConnectionId, selectedSpaceId),
        );
      } catch {
        setFolders([]);
        setHasError(true);
      } finally {
        setFoldersLoaded(true);
      }
    };

    void loadFolders();
  }, [editingConnectionId, isVisible, model.boardId, selectedSpaceId]);

  useEffect(() => {
    const loadLists = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId || !selectedSpaceId) {
        setLists([]);
        setListsLoaded(true);
        return;
      }

      const clickUpApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<ClickUpSourceSettingsApi>(
          SourceTypeIds.ClickUp,
        );
      if (!clickUpApi) {
        setLists([]);
        setListsLoaded(true);
        return;
      }

      setListsLoaded(false);
      setHasError(false);

      try {
        const selectedFolderFromState =
          selectedFolderId === ""
            ? undefined
            : folders.find((folder) => normalizeId(folder.id) === selectedFolderId);

        const selectedFolderFromConfiguration =
          selectedFolderId === "" ||
          normalizeId(configurationRef.current.folder?.id) !== selectedFolderId
            ? undefined
            : configurationRef.current.folder;

        const selectedFolder =
          selectedFolderFromState ?? selectedFolderFromConfiguration;

        const nextLists = selectedFolder?.lists?.length
          ? selectedFolder.lists
          : await clickUpApi.getLists(model.boardId, editingConnectionId, selectedSpaceId);

        setLists(nextLists);

        const selectionKey = `${editingConnectionId}:${selectedSpaceId}:${selectedFolderId || NONE_OPTION}`;
        if (
          !selectedListId &&
          nextLists.length > 0 &&
          autoSelectedListForSelectionRef.current !== selectionKey
        ) {
          autoSelectedListForSelectionRef.current = selectionKey;
          updateConnectionConfiguration<ClickUpConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            (cfg) =>
              normalizeId(cfg.space?.id) !== selectedSpaceId ||
              normalizeId(cfg.folder?.id) !== selectedFolderId ||
              normalizeId(cfg.list?.id)
                ? cfg
                : {
                    ...cfg,
                    list: nextLists[0],
                    statuses: [],
                    doneStatus: undefined,
                  },
            { isDefault: true },
          );
        }
      } catch {
        setLists([]);
        setHasError(true);
      } finally {
        setListsLoaded(true);
      }
    };

    void loadLists();
  }, [
    editingConnectionId,
    folders,
    isVisible,
    model.boardId,
    selectedFolderId,
    selectedListId,
    selectedSpaceId,
  ]);

  useEffect(() => {
    const loadStatuses = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId || !selectedListId) {
        setStatuses([]);
        setStatusesLoaded(true);
        return;
      }

      const clickUpApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<ClickUpSourceSettingsApi>(
          SourceTypeIds.ClickUp,
        );
      if (!clickUpApi) {
        setStatuses([]);
        setStatusesLoaded(true);
        return;
      }

      setStatusesLoaded(false);
      setHasError(false);
      try {
        const selectedList = lists.find((list) => normalizeId(list.id) === selectedListId);
        const nextStatuses =
          selectedList?.statuses && selectedList.statuses.length > 0
            ? selectedList.statuses
            : (
                await clickUpApi.getList(
                  model.boardId,
                  editingConnectionId,
                  selectedListId,
                )
              ).statuses ?? [];

        setStatuses(nextStatuses);

        const currentConfiguration = configurationRef.current;
        const hasExistingStatuses = (currentConfiguration.statuses ?? []).length > 0;
        const hasExistingDoneStatus = Boolean(currentConfiguration.doneStatus);
        const shouldSeedDefaults = !hasExistingStatuses || !hasExistingDoneStatus;
        const selectionKey = `${editingConnectionId}:${selectedListId}`;

        if (
          shouldSeedDefaults &&
          seededDefaultStatusesForSelectionRef.current !== selectionKey
        ) {
          seededDefaultStatusesForSelectionRef.current = selectionKey;

          const defaultStatuses = nextStatuses.filter(
            (status) => status.type !== "closed",
          );
          const defaultDoneStatus = nextStatuses.find(
            (status) => status.type === "closed",
          );

          updateConnectionConfiguration<ClickUpConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            (cfg) =>
              normalizeId(cfg.list?.id) !== selectedListId
                ? cfg
                : {
                    ...cfg,
                    statuses:
                      (cfg.statuses ?? []).length > 0 ? cfg.statuses : defaultStatuses,
                    doneStatus: cfg.doneStatus ?? defaultDoneStatus,
                  },
            { isDefault: true },
          );
        }
      } catch {
        setStatuses([]);
        setHasError(true);
      } finally {
        setStatusesLoaded(true);
      }
    };

    void loadStatuses();
  }, [
    editingConnectionId,
    isVisible,
    lists,
    model.boardId,
    selectedListId,
  ]);

  return {
    isVisible,
    workspaces,
    spaces,
    folders,
    lists,
    statuses,
    workspacesLoaded,
    spacesLoaded,
    foldersLoaded,
    listsLoaded,
    statusesLoaded,
    hasError,
    selectedWorkspaceId: selectedWorkspaceId || NONE_OPTION,
    selectedWorkspaceName:
      selectedWorkspaceId === NONE_OPTION
        ? ""
        : workspaces.find(
            (workspace) => normalizeId(workspace.id) === selectedWorkspaceId,
          )?.name || "",
    selectedSpaceId: selectedSpaceId || NONE_OPTION,
    selectedSpaceName:
      selectedSpaceId === NONE_OPTION
        ? ""
        : spaces.find((space) => normalizeId(space.id) === selectedSpaceId)?.name || "",
    selectedFolderId: selectedFolderId || NONE_OPTION,
    selectedFolderName:
      selectedFolderId === NONE_OPTION
        ? ""
        : folders.find((folder) => normalizeId(folder.id) === selectedFolderId)?.name ||
          "",
    selectedListId: selectedListId || NONE_OPTION,
    selectedListName:
      selectedListId === NONE_OPTION
        ? ""
        : lists.find((list) => normalizeId(list.id) === selectedListId)?.name || "",
    selectedStatusIds: (configuration.statuses ?? []).map((status) =>
      normalizeId(status.id),
    ),
    selectedDoneStatusId: normalizeId(configuration.doneStatus?.id) || NONE_OPTION,
    selectedDoneStatusName:
      normalizeId(configuration.doneStatus?.id) === ""
        ? ""
        : statuses.find(
            (status) =>
              normalizeId(status.id) === normalizeId(configuration.doneStatus?.id),
          )?.status || "",
    assignedToMe: configuration.assignedToMe ?? false,
    noneOptionValue: NONE_OPTION,
    onSelectWorkspace: (workspaceId?: string) => {
      const selectedId = String(workspaceId || NONE_OPTION);
      const selectedWorkspace = workspaces.find(
        (workspace) => normalizeId(workspace.id) === selectedId,
      );

      updateConnectionConfiguration<ClickUpConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          workspace: selectedId === NONE_OPTION ? undefined : selectedWorkspace,
          space: undefined,
          folder: undefined,
          list: undefined,
          statuses: [],
          doneStatus: undefined,
        }),
      );
      seededDefaultStatusesForSelectionRef.current = null;
    },
    onSelectSpace: (spaceId?: string) => {
      const selectedId = String(spaceId || NONE_OPTION);
      const selectedSpace = spaces.find(
        (space) => normalizeId(space.id) === selectedId,
      );

      updateConnectionConfiguration<ClickUpConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          space: selectedId === NONE_OPTION ? undefined : selectedSpace,
          folder: undefined,
          list: undefined,
          statuses: [],
          doneStatus: undefined,
        }),
      );
      seededDefaultStatusesForSelectionRef.current = null;
    },
    onSelectFolder: (folderId?: string) => {
      const selectedId = String(folderId || NONE_OPTION);
      const selectedFolder = folders.find(
        (folder) => normalizeId(folder.id) === selectedId,
      );

      updateConnectionConfiguration<ClickUpConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          folder: selectedId === NONE_OPTION ? undefined : selectedFolder,
          list: undefined,
          statuses: [],
          doneStatus: undefined,
        }),
      );
      seededDefaultStatusesForSelectionRef.current = null;
    },
    onSelectList: (listId?: string) => {
      const selectedId = String(listId || NONE_OPTION);
      const selectedList = lists.find((list) => normalizeId(list.id) === selectedId);

      updateConnectionConfiguration<ClickUpConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          list: selectedId === NONE_OPTION ? undefined : selectedList,
          statuses: [],
          doneStatus: undefined,
        }),
      );
      seededDefaultStatusesForSelectionRef.current = null;
    },
    onSelectStatuses: (statusIds: string[]) => {
      const selectedStatuses = statuses.filter((status) =>
        statusIds.includes(normalizeId(status.id)),
      );

      updateConnectionConfiguration<ClickUpConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          statuses: selectedStatuses,
        }),
      );
    },
    onSelectDoneStatus: (statusId?: string) => {
      const selectedId = String(statusId || NONE_OPTION);
      const selectedDoneStatus = statuses.find(
        (status) => normalizeId(status.id) === selectedId,
      );

      updateConnectionConfiguration<ClickUpConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          doneStatus: selectedId === NONE_OPTION ? undefined : selectedDoneStatus,
        }),
      );
    },
    onToggleAssignedToMe: (value: boolean) => {
      updateConnectionConfiguration<ClickUpConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          assignedToMe: value,
        }),
      );
    },
  };
};
